import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { selectAutoPay } from "store/payments/payments-slice";

import { PaymentMethod } from "store/paymentMethods/paymentMethods-state";
import { updateSlide } from "store/common/common-slice";

import { useRootDispatch, useRootSelector } from "store/hooks";
import {
  maskCardString,
  maskString,
  toInitUpperCase,
  typeMapper,
  move,
  getCardName,
  getWalletName,
  PAYMENT_METHOD_TYPE,
} from "utility";

import classes from "./CardStack.module.scss";

export interface CardStackProps {
  data: PaymentMethod[];
}

type IconPlacement = "header" | "footer";

const paymentMethodIcons: Partial<Record<PAYMENT_METHOD_TYPE, string>> = {
  [PAYMENT_METHOD_TYPE.card]: "credit_card",
  [PAYMENT_METHOD_TYPE.eWallet]: "account_balance_wallet",
};

export const CardStack: React.FC<CardStackProps> = ({ data }) => {
  const { t } = useTranslation(["common", "app"]);
  const autoPayInfo = useRootSelector(selectAutoPay);
  const autoPayMethods = autoPayInfo
    ?.filter((p) => p.status === 1)
    .map((p) => p.paymentMethodId);

  const dispatch = useRootDispatch();

  const initialCards = data.map((paymentMethod, index) => ({
    ...paymentMethod,
    colorClass: classes[`CardColor${index + 1}`],
  }));

  const [cards, setCards] = useState(initialCards);

  useEffect(() => {
    if (data.length !== cards.length) {
      const newCardData = data.map((paymentMethod, index) => ({
        ...paymentMethod,
        colorClass: classes[`CardColor${index + 1}`],
      }));
      setCards(newCardData);
    }
  }, [data, cards]);

  const moveToEnd = (from: number) => {
    // click disabled for the 1st card
    if (from) {
      setCards(move(cards, from, 0));
    }
  };

  const renderIconElements = (payment: PaymentMethod, type: IconPlacement) => {
    const iconName =
      paymentMethodIcons[payment.paymentMethodType as PAYMENT_METHOD_TYPE] ??
      "account_balance";

    return (
      <ft-icon
        class={[
          classes.Logo,
          type === "footer" ? classes.IconFooter : classes.IconSize,
        ].join(" ")}
      >
        {iconName}
      </ft-icon>
    );
  };

  const renderCards = (methods: PaymentMethod[]) => {
    return methods.map((paymentMethod, index) => {
      const canDrag = index === 0;
      let title = paymentMethod.paymentMethodName
        ? paymentMethod.paymentMethodName
        : "";
      let bankName = `${paymentMethod.bankName}`;
      if (!title && paymentMethod?.bankAccountType) {
        title = `${bankName} ${
          paymentMethod.bankAccountType
            ? typeMapper(paymentMethod.bankAccountType, "accountReverse")
            : ""
        }`;
      }
      let detailLine = "";
      let cardNo = "";
      if (paymentMethod.paymentMethodType === 1) {
        detailLine = `${bankName} ${
          paymentMethod.bankAccountType
            ? typeMapper(paymentMethod.bankAccountType, "accountReverse")
            : ""
        }`;
        cardNo = paymentMethod.accountNumber
          ? maskString(paymentMethod.accountNumber)
          : "";
      } else if (paymentMethod.paymentMethodType === 2) {
        bankName = getCardName(paymentMethod.cardType!);
        detailLine = `${bankName} Card `;
        cardNo = paymentMethod.lastFour
          ? maskCardString(paymentMethod.lastFour)
          : "";
      } else if (paymentMethod.paymentMethodType === 3) {
        let walletCardType = paymentMethod.cardType
          ? getCardName(paymentMethod.cardType)
          : "";
        if (!walletCardType) {
          walletCardType = "Card";
        }
        bankName = getWalletName(paymentMethod.walletType!);
        detailLine = `${bankName} ${walletCardType} `;
        cardNo = paymentMethod.tokenizedCardData
          ? maskCardString(paymentMethod.tokenizedCardData)
          : "";
      }

      return (
        <div
          key={paymentMethod.id}
          className={[classes.CardStackStyle, paymentMethod.colorClass].join(
            " ",
          )}
          style={{
            zIndex: cards.length - index,
            cursor: canDrag ? "auto" : "pointer",
          }}
          onClick={() => moveToEnd(index)}
        >
          <div className={classes.CardContainer}>
            {index === 0 ? (
              <ft-icon
                class={classes.Item_icon}
                title={t("common:manage_saved_payment")}
                onClick={() => {
                  dispatch(updateSlide({ id: "ManageWallet", tab: 0 }));
                }}
              >
                settings
              </ft-icon>
            ) : null}
            <div className={classes.CardHeader}>
              <div>
                <p className={classes.CardName}>{toInitUpperCase(title)}</p>
                <span className={classes.CardBankName}>{detailLine}</span>
              </div>
              <div>{renderIconElements(paymentMethod, "header")}</div>
            </div>
            <div className={classes.CardFooter}>
              <span className={classes.AccountNumber}>
                {renderIconElements(paymentMethod, "footer")}
                {cardNo}
              </span>
              <span className={classes.AccountNumber}>
                {t("common:autopay_label")}{" "}
                {autoPayMethods?.includes(paymentMethod.id!) ? (
                  <b>{t("common:on")}</b>
                ) : (
                  <b>{t("common:off")}</b>
                )}
              </span>
            </div>
            <div
              key={"shine" + paymentMethod.id}
              className={[
                classes.CardStackShine,
                classes.CardStackShineOne,
              ].join(" ")}
            ></div>
            <div
              key={"shine-next" + paymentMethod.id}
              className={[
                classes.CardStackShine,
                classes.CardStackShineTwo,
              ].join(" ")}
            ></div>
          </div>
        </div>
      );
    });
  };

  const stackHeight = 185 + 30 * (cards.length - 1);

  return (
    <div className={classes.CardStackWrapper}>
      <div
        style={{ height: stackHeight + "px" }}
        className={classes.CardStackWrapStyle}
      >
        {renderCards(cards)}
      </div>
    </div>
  );
};
